import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../../utils/api";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import he from "he";
import { Button, Spinner } from "react-bootstrap";
import parse from "html-react-parser";
import { FiChevronRight, FiCheckCircle, FiXCircle, FiEye, FiEyeOff, FiHelpCircle, FiAward, FiClipboard, FiTrendingUp } from 'react-icons/fi';

const ViewResult = () => {
  const { paperId, studentId, examRecordId } = useParams();
  const [sectionSummary, setSectionSummary] = useState([]);


  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnswers, setShowAnswers] = useState({});
  const [examSummary, setExamSummary] = useState({
    totalQuestions: 0,
    correctAnswers: 0,
    wrongAnswers: 0,
    unansweredAnswers: 0,
    totalAnsweredQuestions: 0,
    totalMarks: 0,
    examStatus: "",
    endTime: ""
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  async function fetchQuestions() {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
      fd.append("token", localStorage.getItem("authToken"));
      fd.append("programType", "getOverViewOfExamForWeb");
      fd.append("paperId", paperId);
      fd.append("id", examRecordId);

      const response = await axiosInstance.post(
        "examPaper/exam",
        fd
      );

      console.log("API Response:", response);

      if (response.data && response.data.questions) {
        const data = response.data;

        if (data.questions && Array.isArray(data.questions)) {
          setQuestions(data.questions);
        }

        const unansweredCount =
          data.questions?.filter(
            q => q.isCorrect === 2 || q.isCorrect === "2"
          ).length || 0;

        setExamSummary({
          totalQuestions: parseInt(data.totalQuestions) || data.questions.length,
          correctAnswers: parseInt(data.correctAnswers) || 0,
          wrongAnswers: parseInt(data.wrongAnswers) || 0,
          unansweredAnswers: unansweredCount,
          totalAnsweredQuestions: parseInt(data.totalAnsweredQuestions) || 0,
          totalMarks: data.summary?.totalMarks || 0,
          examStatus: data.examStatus || "",
          endTime: data.endTime || ""
        });

        setSectionSummary(data.summary?.sections || []);

      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  }

  const renderLatexText = (text, layout = "single", fontSize = "16px") => {
    if (!text) return null;

    if (typeof text !== "string") {
      if (text && typeof text.toString === "function") {
        text = text.toString();
      } else {
        return null;
      }
    }

    let decodedText = he.decode(text);
    decodedText = decodedText.replace(/<br\s*\/?>/gi, "\n");

    const latexAndImageRegex =
      /\\\((.*?)\\\)|\\\[(.*?)\\\]|<img[^>]*\bsrc=(["'])(.*?)\3[^>]*>|\\begin{align}([\s\S]*?)\\end{align}/g;

    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = latexAndImageRegex.exec(decodedText)) !== null) {
      const [fullMatch, inlineLatex, blockLatex, , imageSrc, alignContent] = match;
      const matchIndex = match.index;

      if (matchIndex > lastIndex) {
        const plainText = decodedText.slice(lastIndex, matchIndex);
        plainText.split("\n").forEach((line, i, arr) => {
          if (line) parts.push(<span key={`html-${key++}`}>{parse(line)}</span>);
          if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
        });
      }

      if (imageSrc) {
        parts.push(
          <img
            key={`img-${key++}`}
            src={imageSrc}
            alt="Image"
            style={{
              maxWidth: `calc(${fontSize} * 18)`,
              display: "inline",
              verticalAlign: "middle",
              marginLeft: "4px",
              marginRight: "4px",
            }}
          />
        );
      } else if (alignContent) {
        parts.push(
          <BlockMath key={`align-${key++}`}>
            {`\\begin{align}${alignContent}\\end{align}`}
          </BlockMath>
        );
      } else if (inlineLatex || blockLatex) {
        parts.push(
          <InlineMath key={`inline-${key++}`}>
            {inlineLatex || blockLatex}
          </InlineMath>
        );
      }

      lastIndex = matchIndex + fullMatch.length;
    }

    if (lastIndex < decodedText.length) {
      const remaining = decodedText.slice(lastIndex);
      remaining.split("\n").forEach((line, i, arr) => {
        if (line) parts.push(<span className="normal-inline" key={`html-${key++}`}>{parse(line)}</span>);
        if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
      });
    }

    return <span style={{ whiteSpace: "normal", display: "inline" }}>{parts}</span>;
  };

  const toggleShowAnswer = (questionId) => {
    setShowAnswers((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const getMarksBackgroundColor = (marks) => {
    const marksNum = parseFloat(marks);
    if (marksNum > 0) {
      return "#d4edda";
    } else if (marksNum < 0) {
      return "#f8d7da";
    }
    return "#ECF2FE";
  };

  const MarksDisplay = ({ marks }) => {
    const marksNum = parseFloat(marks);
    const bgColor = getMarksBackgroundColor(marks);

    return (
      <div
        className="marks-badge"
        style={{
          backgroundColor: bgColor,
          color: marksNum > 0 ? "#155724" : marksNum < 0 ? "#721c24" : "#000",
          fontWeight: "bold",
        }}
      >
        Marks: {marksNum > 0 ? `+${marksNum}` : marksNum}
      </div>
    );
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "-";

    // Convert "YYYY-MM-DD HH:mm:ss" → ISO format
    const isoString = dateTimeStr.replace(" ", "T");
    const date = new Date(isoString);

    return date.toLocaleString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };



  if (loading) {
    return (
      <div className='dashboard-container'
        style={{
          position: "fixed",
          inset: 0,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: "2.5rem 3rem",
            borderRadius: "1.25rem",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
            border: "1px solid #e5e7eb",
            width: "90%",
            maxWidth: "360px",
            marginLeft: "250px"
          }}
        >
          {/* Spinner */}
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #3b82f6",
              margin: "0 auto",
              animation: "spin 1s linear infinite",
            }}
          />

          <h3
            style={{
              marginTop: "1rem",
              marginBottom: "0.25rem",
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#1f2937",
            }}
          >
            Loading
          </h3>

          <p
            style={{
              fontSize: "0.875rem",
              color: "#64748b",
              marginBottom: "1.5rem",
            }}
          >
            Fetching analytics & insights…
          </p>

          {/* Skeleton */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {["80%", "60%", "90%"].map((w, i) => (
              <div
                key={i}
                style={{
                  height: "10px",
                  width: w,
                  margin: "0 auto",
                  borderRadius: "999px",
                  background:
                    "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%)",
                  backgroundSize: "400% 100%",
                  animation: "shimmer 1.4s ease infinite",
                }}
              />
            ))}
          </div>

          {/* Inline keyframes */}
          <style>
            {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes shimmer {
              0% { background-position: 100% 0; }
              100% { background-position: -100% 0; }
            }
          `}
          </style>
        </div>
      </div>
    );
  }

  return (
    <div className="results-container">


      <div className="results-content-grid">
        {/* Summary Sidebar */}
        <div className="summary-sidebar">
          <div className="summary-card">
            <div className="summary-header">
              <h3><FiAward /> Result Summary</h3>
            </div>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">Exam Status</span>
                <span className="stat-value">{examSummary.examStatus}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Questions</span>
                <span className="stat-value">{examSummary.totalQuestions}</span>
              </div>
             
              <div className="stat-item">
                <span className="stat-label">Answered</span>
                <span className="stat-value">
                  {examSummary.totalAnsweredQuestions}
                </span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Correct Answers</span>
                <span className="stat-value correct">
                  {examSummary.correctAnswers}
                </span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Wrong Answers</span>
                <span className="stat-value wrong">
                  {examSummary.wrongAnswers}
                </span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Unanswered</span>
                <span
                  className="stat-value"
                  style={{ color: "#d97706" }}
                >
                  {examSummary.unansweredAnswers}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Marks</span>
                <span className="stat-value marks">{examSummary.totalMarks}</span>
              </div>
              {examSummary.endTime && (
                <div className="stat-item">
                  <span className="stat-label">End Time</span>
                  <span className="stat-value">
                    {formatDateTime(examSummary.endTime)}
                  </span>

                </div>
              )}
            </div>

            {sectionSummary.length > 0 && (
              <div className="section-performance">
                <div className="section-header">
                  <h4><FiTrendingUp /> Section-wise Performance</h4>
                </div>
                <div className="sections-list">
                  {sectionSummary.map((section, index) => (
                    <div key={section.sectionId} className="section-item">
                      <div className="section-title">
                        {section.sectionName || `Section ${section.sectionId}`}
                      </div>

                      <div className="section-stats">
                        <div className="section-stat">
                          <FiCheckCircle className="stat-icon correct" />
                          <span>{section.correct} Correct</span>
                        </div>
                        <div className="section-stat">
                          <FiXCircle className="stat-icon wrong" />
                          <span>{section.wrong} Incorrect</span>
                        </div>
                        <div className="section-stat">
                          <FiClipboard className="stat-icon total" />
                          <span>{section.total} Total</span>
                        </div>
                      </div>
                      <div className="section-marks">
                        <strong>Total:</strong> {section.marks} Marks
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Questions Main Content */}
        <div className="questions-main">
          {questions.map((question, index) => {
            const isCorrect =
              question.isCorrect === 1 ||
              question.isCorrect === "1" ||
              question.isCorrect === true;

            const isUnanswered =
              question.isCorrect === 2 ||
              question.isCorrect === "2";
            const selectedOption = question.selectOption;
            const correctOption = question.correctOption;



            return (
              <div key={question.questionId} className="question-result-card">
                <div className="question-header">
                  <div className="question-meta">
                    <div className="question-id-badge">
                      ID: {question.questionId}
                    </div>
                    <MarksDisplay marks={question.marks} />
                    <div
                      className={`status-badge ${isCorrect ? "correct" : isUnanswered ? "unanswered" : "wrong"
                        }`}
                    >
                      {isCorrect ? (
                        <FiCheckCircle />
                      ) : isUnanswered ? (
                        <FiHelpCircle />
                      ) : (
                        <FiXCircle />
                      )}

                      {isCorrect ? "Correct" : isUnanswered ? "Unanswered" : "Wrong"}
                    </div>
                  </div>

                  <div className="question-content">
                    <div className="question-number">{index + 1}.</div>
                    <div className="question-text">
                      {renderLatexText(question.question)}
                    </div>
                  </div>

                  {question.options && question.options.length > 0 ? (
                    <>
                      <div className="options-section">
                        <div className="options-header">Options</div>
                        <div
                          className="options-grid"
                          style={{
                            gridTemplateColumns: question.options.some(
                              (opt) => opt.length > 50
                            )
                              ? "1fr"
                              : "1fr 1fr",
                          }}
                        >
                          {question.options.map((option, idx) => {
                            const optionNumber = idx + 1;
                            const selectedOptionNum =
                              question.selectOption !== null
                                ? Number(question.selectOption)
                                : null;
                            const correctOptionNum =
                              question.correctOption !== null
                                ? Number(question.correctOption)
                                : null;
                            const isSelected =
                              !isUnanswered &&
                              selectedOptionNum === optionNumber;
                            const isCorrectOption = correctOptionNum === optionNumber;

                            return (
                              <div
                                key={idx}
                                className={`option-item ${isCorrectOption ? 'correct-option' :
                                  isSelected ? 'wrong-option' : ''
                                  }`}
                              >
                                <div className="option-letter">{optionNumber}</div>
                                <div className="option-text">
                                  {renderLatexText(option)}
                                </div>
                                {isCorrectOption && (
                                  <FiCheckCircle className="option-icon correct" />
                                )}
                                {isSelected && !isCorrectOption && (
                                  <FiXCircle className="option-icon wrong" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="answer-section">
                      <div className="answer-row">
                        <strong>Your Answer:</strong>
                        <span className="answer-text">
                          {selectedOption !== null && selectedOption !== undefined
                            ? selectedOption
                            : "_ _ _ _ _"}
                        </span>
                      </div>
                      <div className="answer-row">
                        <strong>Correct Answer:</strong>
                        <span className="answer-text correct">
                          {correctOption !== null && correctOption !== undefined
                            ? correctOption
                            : "_ _ _ _ _"}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="question-actions">
                    <button
                      onClick={() => toggleShowAnswer(question.questionId)}
                      className="toggle-answer-button"
                    >
                      {showAnswers[question.questionId] ? (
                        <>
                          <FiEyeOff /> Hide Explanation
                        </>
                      ) : (
                        <>
                          <FiEye /> Show Explanation
                        </>
                      )}
                    </button>
                  </div>

                  {showAnswers[question.questionId] && (
                    <div className="explanation-section">
                      <div className="explanation-header">
                        <FiHelpCircle />
                        <strong>Explanation</strong>
                      </div>
                      <div className="explanation-content">
                        {renderLatexText(question.explanation)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>
        {`
          .results-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
            padding: 1.5rem;
          }

          .breadcrumb-navigation {
            margin-bottom: 2rem;
          }

          .breadcrumb-navigation ul {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            list-style: none;
            padding: 0;
            margin: 0;
          }
            .status-badge.unanswered {
  background: #fff3cd;
  color: #856404;
}

          .breadcrumb-link {
            font-size: 0.875rem;
            color: #6b7280;
            text-decoration: none;
            transition: color 0.2s;
          }

          .breadcrumb-link:hover {
            color: #3b82f6;
          }

          .breadcrumb-separator {
            color: #9ca3af;
            font-size: 0.875rem;
          }

          .breadcrumb-current {
            font-size: 0.875rem;
            color: #3b82f6;
            font-weight: 600;
          }

          .results-content-grid {
          
            margin: 0 auto;
            display: grid;
            grid-template-columns: 300px 1fr;
            gap: 1.5rem;
          }

          .summary-sidebar {
            position: sticky;
            top: 1.5rem;
            height: fit-content;
          }

          .summary-card {
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            overflow: hidden;
          }

          .summary-header {
            padding: 1.25rem;
            border-bottom: 1px solid #f3f4f6;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .summary-header h3 {
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0;
          }

          .summary-stats {
            padding: 1.25rem;
          }

          .stat-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 0;
            border-bottom: 1px solid #f9fafb;
          }

          .stat-item:last-child {
            border-bottom: none;
          }

          .stat-label {
            font-size: 0.875rem;
            color: #6b7280;
          }

          .stat-value {
            font-size: 0.875rem;
            font-weight: 600;
            color: #1f2937;
          }

          .stat-value.correct {
            color: #10b981;
          }

          .stat-value.wrong {
            color: #ef4444;
          }

          .stat-value.marks {
            color: #3b82f6;
          }

          .section-performance {
            border-top: 1px solid #f3f4f6;
            padding: 1.25rem;
          }

          .section-header {
            margin-bottom: 1rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .section-header h4 {
            font-size: 0.9375rem;
            font-weight: 600;
            color: #1f2937;
            margin: 0;
          }

          .sections-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .section-item {
            background: #f9fafb;
            border-radius: 0.75rem;
            padding: 1rem;
            border: 1px solid #f3f4f6;
          }

          .section-title {
            font-size: 0.875rem;
            font-weight: 600;
            color: #374151;
            margin-bottom: 0.75rem;
          }

          .section-stats {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-bottom: 0.75rem;
          }

          .section-stat {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.8125rem;
            color: #4b5563;
          }

          .stat-icon {
            font-size: 0.875rem;
          }

          .stat-icon.correct {
            color: #10b981;
          }

          .stat-icon.wrong {
            color: #ef4444;
          }

          .stat-icon.total {
            color: #6b7280;
          }

          .section-marks {
            font-size: 0.8125rem;
            color: #3b82f6;
            font-weight: 500;
          }

          .questions-main {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .question-result-card {
            background: white;
            border-radius: 1rem;
            border: 1px solid #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            padding: 1.5rem;
            transition: all 0.3s;
          }

          .question-result-card:hover {
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
            border-color: #cbd5e0;
          }

          .question-meta {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1rem;
            flex-wrap: wrap;
          }

          .question-id-badge {
            background: #fef3c7;
            color: #92400e;
            padding: 0.25rem 0.75rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
          }

          .marks-badge {
            padding: 0.25rem 0.75rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
          }

          .status-badge {
            padding: 0.25rem 0.75rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
          }

          .status-badge.correct {
            background: #d1fae5;
            color: #065f46;
          }

          .status-badge.wrong {
            background: #fee2e2;
            color: #991b1b;
          }

          .question-content {
            display: flex;
            gap: 0.5rem;
            margin-bottom: 1.5rem;
          }

          .question-number {
            font-size: 1rem;
            font-weight: 700;
            color: #3b82f6;
          }

          .question-text {
            flex: 1;
            font-size: 0.9375rem;
            line-height: 1.6;
            color: #1f2937;
          }

          .options-section {
            margin-bottom: 1.5rem;
          }

          .options-header {
            font-size: 0.875rem;
            font-weight: 600;
            color: #374151;
            margin-bottom: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .options-grid {
            display: grid;
            gap: 0.75rem;
          }

          .option-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.875rem;
            background: #f9fafb;
            border-radius: 0.5rem;
            border: 1px solid #e5e7eb;
            position: relative;
          }

          .option-item.correct-option {
            background: #d1fae5;
            border-color: #a7f3d0;
          }

          .option-item.wrong-option {
            background: #fee2e2;
            border-color: #fecaca;
          }

          .option-letter {
            width: 1.5rem;
            height: 1.5rem;
            background: #e5e7eb;
            color: #374151;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 0.75rem;
            flex-shrink: 0;
          }

          .option-item.correct-option .option-letter {
            background: #a7f3d0;
            color: #065f46;
          }

          .option-item.wrong-option .option-letter {
            background: #fecaca;
            color: #991b1b;
          }

          .option-text {
            flex: 1;
            font-size: 0.875rem;
            line-height: 1.5;
            color: #1f2937;
          }

          .option-icon {
            position: absolute;
            right: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
          }

          .option-icon.correct {
            color: #10b981;
          }

          .option-icon.wrong {
            color: #ef4444;
          }

          .answer-section {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
          }

          .answer-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem;
            background: #f9fafb;
            border-radius: 0.5rem;
            border: 1px solid #f3f4f6;
          }

          .answer-text {
            font-size: 0.875rem;
            color: #4b5563;
          }

          .answer-text.correct {
            color: #10b981;
            font-weight: 600;
          }

          .question-actions {
            border-top: 1px solid #f3f4f6;
            padding-top: 1rem;
            display: flex;
            justify-content: flex-end;
          }

          .toggle-answer-button {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .toggle-answer-button:hover {
            background: #2563eb;
            transform: translateY(-1px);
          }

          .explanation-section {
            margin-top: 1.5rem;
            padding: 1rem;
            background: #f0f9ff;
            border-radius: 0.5rem;
            border: 1px solid #e0f2fe;
          }

          .explanation-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.75rem;
            color: #0369a1;
            font-size: 0.875rem;
            font-weight: 600;
          }

          .explanation-content {
            font-size: 0.875rem;
            line-height: 1.6;
            color: #4b5563;
          }

          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
          }

          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e0f2fe;
            border-top: 3px solid #0369a1;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          @media (max-width: 1024px) {
            .results-content-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }

            .summary-sidebar {
              position: static;
            }
          }

          @media (max-width: 768px) {
            .results-container {
              padding: 1rem;
            }

            .options-grid {
              grid-template-columns: 1fr !important;
            }

            .question-meta {
              flex-direction: column;
              align-items: flex-start;
            }

            .question-content {
              flex-direction: column;
              gap: 0.25rem;
            }
          }

          @media (max-width: 480px) {
            .question-result-card {
              padding: 1rem;
            }

            .summary-card,
            .section-item {
              padding: 0.75rem;
            }

            .toggle-answer-button {
              width: 100%;
              justify-content: center;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ViewResult;